import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import "react-datepicker/dist/react-datepicker.css";
import { createPortal } from "react-dom";
import axios from "axios";
import { getApiUrl } from "../config/api";
import { checkTokenValidity } from "../utils/auth";
import LoadingScreen from "../components/LoadingScreen";
import DatePickerField from "../components/DatePickerField";
import OrderSearchModal from "../components/OrderSearchModal";
import LoadingOverlay from "../components/LoadingOverlay";

// Helper functions
const TextField = ({
	label,
	value,
	onChange,
	className = "",
	id,
	disabled = false,
	width = "full",
	rightIcon,
	onClick,
	maxLength,
	forceWhite = false,
	readOnly = false,
}) => (
	<div className={className}>
		<label className="text-sm font-medium text-gray-500" htmlFor={id}>
			{label}
		</label>
		<div className="relative">
			<input
				id={id}
				type="text"
				value={value || ""}
				onChange={(e) => onChange(e.target.value.slice(0, maxLength))}
				disabled={disabled}
				onClick={onClick}
				maxLength={maxLength}
				className={`mt-0.5 block w-${width} px-2 py-1 border border-gray-300 rounded-md shadow-sm text-sm
					${
						disabled && !forceWhite
							? "bg-gray-100 text-gray-500 cursor-not-allowed"
							: "bg-white text-gray-900"
					} 
					${rightIcon ? "pr-8" : ""}`}
				readOnly={readOnly}
			/>
			{rightIcon && (
				<div className="absolute inset-y-0 right-0 flex items-center pr-2 mt-0.5">
					{rightIcon}
				</div>
			)}
		</div>
	</div>
);

const MaterialSearchIcon = ({ onClick }) => (
	<button
		onClick={onClick}
		className="w-4 h-4 text-gray-500 hover:text-gray-700"
	>
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth={1.5}
			stroke="currentColor"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
			/>
		</svg>
	</button>
);

const stripLeadingZeros = (str) => {
	if (!str) return "";
	return str.replace(/^0+/, "");
};

const MaterialSearchModal = ({
	isOpen,
	onClose,
	onSelect,
	plant,
	storageLocation,
	type,
	existingItems,
	category,
	navigate
}) => {
	const [materials, setMaterials] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [filteredMaterials, setFilteredMaterials] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 10;

	// Create a memoized fetch function
	const fetchMaterials = useCallback(async () => {
		if (!isOpen || !storageLocation) {
			return;
		}

		try {
			setIsLoading(true);
			const response = await axios.get(
				getApiUrl(`/materials/${storageLocation}`),
				{
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				}
			);

			const data = response.data;
			const transformedData = data.map((item) => ({
				id: item.matnr,
				material: item.matnr,
				displayMaterial: stripLeadingZeros(item.matnr),
				description: item.maktx,
				quantity: item.menge || 0,
				uom: item.uom || "ST",
				type: item.type,
				plant: item.werks,
				storage_location: item.lgort,
				batch: item.charg || "",
				displayBatch: item.charg ? stripLeadingZeros(item.charg) : "",
			}));

			// Update filtering logic based on type
			const availableMaterials = transformedData.filter((material) => {
				if (type === "V") {
					return material.type === "V";
				} else if (type === "NV") {
					return material.type !== "V";
				}
				return true;
			});

			setMaterials(availableMaterials);
			setFilteredMaterials(availableMaterials);
		} catch (error) {
			// console.error("Error fetching materials:", error);
			// Handle authentication errors
			if (error.response?.status === 401) {
				localStorage.removeItem("token");
				navigate("/login");
			}
		} finally {
			setIsLoading(false);
		}
	}, [isOpen, storageLocation, type, navigate]);

	useEffect(() => {
		if (isOpen) {
			fetchMaterials();
		}
	}, [isOpen, fetchMaterials]);

	useEffect(() => {
		const filtered = materials.filter(
			(material) =>
				material.material.toLowerCase().includes(searchTerm.toLowerCase()) ||
				material.description.toLowerCase().includes(searchTerm.toLowerCase())
		);
		setFilteredMaterials(filtered);
		setCurrentPage(1);
	}, [searchTerm, materials]);

	const indexOfLastItem = currentPage * itemsPerPage;
	const indexOfFirstItem = indexOfLastItem - itemsPerPage;
	const currentItems = filteredMaterials.slice(
		indexOfFirstItem,
		indexOfLastItem
	);
	const totalPages = Math.ceil(filteredMaterials.length / itemsPerPage);

	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-50 overflow-y-auto">
			<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
				<div
					className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
					onClick={onClose}
				/>

				<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
					<div className="bg-white px-4 pt-5 pb-4 sm:p-6">
						<div className="flex justify-between items-center mb-4">
							<h3 className="text-lg font-medium text-gray-900">
								Select Material
							</h3>
							<button
								onClick={onClose}
								className="text-gray-400 hover:text-gray-500"
							>
								<XMarkIcon className="h-6 w-6" />
							</button>
						</div>

						<div className="mb-4">
							<input
								type="text"
								placeholder="Search material or description..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
							/>
						</div>

						{isLoading ? (
							<div className="text-center py-4">Loading...</div>
						) : (
							<>
								{/* Desktop View - Table */}
								<div className="hidden md:block overflow-x-auto">
									<table className="min-w-full divide-y divide-gray-200">
										<thead className="bg-gray-50">
											<tr>
												<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
													Action
												</th>
												<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
													Material
												</th>
												<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
													Batch
												</th>
												<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
													Description
												</th>
												<th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
													Qty
												</th>
												<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
													UOM
												</th>
											</tr>
										</thead>
										<tbody className="bg-white divide-y divide-gray-200">
											{currentItems.map((material) => (
												<tr
													key={`${material.material}-${material.batch || ""}`}
													className="hover:bg-gray-50 cursor-pointer"
													onClick={() => onSelect(material)}
												>
													<td className="px-3 py-2 whitespace-nowrap">
														<button
															onClick={(e) => {
																e.stopPropagation();
																onSelect(material);
															}}
															className="text-xs text-white bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded"
														>
															Select
														</button>
													</td>
													<td className="px-3 py-2 whitespace-nowrap text-sm">
														<div>
															<div>{material.displayMaterial}</div>
															<div className="text-xs text-gray-500">
																{material.description}
															</div>
														</div>
													</td>
													<td className="px-3 py-2 whitespace-nowrap text-sm">
														<span className="text-sm">
															{material.displayBatch || "-"}
														</span>
													</td>
													<td className="px-3 py-2 text-sm">
														{material.description}
													</td>
													<td className="px-3 py-2 whitespace-nowrap text-sm text-right">
														{material.quantity}
													</td>
													<td className="px-3 py-2 whitespace-nowrap text-sm">
														{material.uom}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>

								{/* Mobile View - Cards */}
								<div className="md:hidden">
									<div className="space-y-3">
										{currentItems.map((material) => (
											<div
												key={`${material.material}-${material.batch || ""}`}
												className="bg-white border rounded-lg shadow-sm hover:bg-gray-50 cursor-pointer"
												onClick={() => onSelect(material)}
											>
												<div className="p-4">
													<div className="flex justify-between items-start mb-2">
														<div>
															<div className="text-sm font-medium text-gray-900">
																{material.displayMaterial}
															</div>
															<div className="text-sm text-gray-500 mt-1">
																{material.description}
															</div>
														</div>
														<button
															onClick={(e) => {
																e.stopPropagation();
																onSelect(material);
															}}
															className="text-xs text-white bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded"
														>
															Select
														</button>
													</div>
													<div className="grid grid-cols-2 gap-2 text-sm">
														<div>
															<span className="text-gray-500">Qty: </span>
															<span>
																{material.quantity} {material.uom}
															</span>
														</div>
														<div className="text-right">
															<span className="text-gray-500">Batch: </span>
															<span>{material.displayBatch || "-"}</span>
														</div>
														<div>
															<span className="text-gray-500">Plant: </span>
															<span>{plant}</span>
														</div>
														<div className="text-right">
															<span className="text-gray-500">SLoc: </span>
															<span>{storageLocation}</span>
														</div>
													</div>
												</div>
											</div>
										))}
									</div>
								</div>
							</>
						)}

						{/* Mobile Pagination */}
						<div className="mt-4 px-2">
							<div className="text-sm text-gray-700 text-center mb-3">
								Showing {indexOfFirstItem + 1} to{" "}
								{Math.min(indexOfLastItem, filteredMaterials.length)} of{" "}
								{filteredMaterials.length} results
							</div>
							<div className="flex items-center justify-center space-x-4">
								<button
									onClick={() => setCurrentPage(currentPage - 1)}
									disabled={currentPage === 1}
									className={`p-2 rounded-full ${
										currentPage === 1
											? "text-gray-400 bg-gray-100"
											: "text-blue-600 hover:bg-blue-50"
									}`}
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-5 w-5"
										viewBox="0 0 20 20"
										fill="currentColor"
									>
										<path
											fillRule="evenodd"
											d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
											clipRule="evenodd"
										/>
									</svg>
								</button>
								<span className="text-sm text-gray-700">
									Page {currentPage} of {totalPages}
								</span>
								<button
									onClick={() => setCurrentPage(currentPage + 1)}
									disabled={currentPage === totalPages}
									className={`p-2 rounded-full ${
										currentPage === totalPages
											? "text-gray-400 bg-gray-100"
											: "text-blue-600 hover:bg-blue-50"
									}`}
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-5 w-5"
										viewBox="0 0 20 20"
										fill="currentColor"
									>
										<path
											fillRule="evenodd"
											d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
											clipRule="evenodd"
										/>
									</svg>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>,
		document.getElementById("portal-root")
	);
};

const StorageLocationModal = ({ isOpen, onClose, onSelect }) => {
	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-50 overflow-y-auto">
			<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
				<div
					className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
					onClick={onClose}
				/>

				<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full">
					<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
						<div className="flex justify-between items-center mb-4">
							<h3 className="text-lg font-medium text-gray-900">
								Select Storage Location
							</h3>
							<button
								onClick={onClose}
								className="text-gray-400 hover:text-gray-500"
							>
								<XMarkIcon className="h-6 w-6" />
							</button>
						</div>
						<div className="space-y-3">
							<button
								onClick={() => onSelect("ST11")}
								className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
							>
								ST11
							</button>
							<button
								onClick={() => onSelect("ST21")}
								className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
							>
								ST21
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>,
		document.getElementById("portal-root")
	);
};

// Add this new component for the clear button
const ClearIcon = ({ onClick }) => (
	<button
		onClick={onClick}
		className="w-4 h-4 text-gray-500 hover:text-gray-700"
	>
		<XMarkIcon className="h-4 w-4" />
	</button>
);

function AddSparepart() {
	const location = useLocation();
	const navigate = useNavigate();
	const { category, materials, type } = location.state || {
		category: "",
		materials: [],
		type: "",
	};
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [isMaterialSearchOpen, setIsMaterialSearchOpen] = useState(false);
	const [isOrderSearchOpen, setIsOrderSearchOpen] = useState(false);
	const [isValidatingToken, setIsValidatingToken] = useState(true);
	const [isStorageLocationOpen, setIsStorageLocationOpen] = useState(false);

	const [mainData, setMainData] = useState({
		plant: "",
		storageLocation: "",
		materialSlip: "",
		docHeaderText: "",
		documentDate: new Date().toISOString().split("T")[0],
	});

	const [items, setItems] = useState([]);
	const [selectedItem, setSelectedItem] = useState(0);

	// Add new state for custom alert
	const [alertDialog, setAlertDialog] = useState({
		isOpen: false,
		message: "",
		type: "success", // or "error"
	});

	// Add loading state
	const [isSaving, setIsSaving] = useState(false);

	// Add new state for new item form
	const [newItemData, setNewItemData] = useState(null);

	// Memoize the navigate callback
	const memoizedNavigate = useCallback((path) => {
		navigate(path);
	}, [navigate]);

	// Create a custom alert function
	const showAlert = (message, type = "error", onClose = null) => {
		setAlertDialog({
			isOpen: true,
			message,
			type,
			onClose
		});
	};

	const handleMainDataChange = (field, value) => {
		setMainData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleItemChange = useCallback((field, value, index) => {
		if (field === "quantity" && !/^\d*\.?\d*$/.test(value)) {
			return;
		}

		setItems((prevItems) =>
			prevItems.map((item, idx) =>
				idx === index ? { ...item, [field]: value } : item
			)
		);
	}, []);

	const handleDateChange = (date) => {
		setMainData((prev) => ({
			...prev,
			documentDate: date.toISOString().split("T")[0],
		}));
	};

	const handleMaterialSearchClick = () => {
		if (isSaving) return;

		if (!mainData.storageLocation || !mainData.plant) {
			showAlert("Please fill in Plant and Storage Location first");
			return;
		}
		setIsMaterialSearchOpen(true);
	};

	const handleDeleteItem = (index) => {
		setItems((prevItems) => {
			const filteredItems = prevItems.filter((_, idx) => idx !== index);
			return filteredItems.map((item, idx) => ({
				...item,
				item: (idx + 1).toString().padStart(2, "0"),
			}));
		});
	};

	const handleAddNewItem = () => {
		setSelectedItem(-1);
		setNewItemData({
			id: Date.now(),
			item: (items.length + 1).toString().padStart(2, "0"),
			material: "",
			materialDescription: "",
			quantity: "",
			uom: "",
			recipient: "",
			order: "",
			text: "",
			batch: "",
		});
	};

	const handleAddItemToList = () => {
		if (!newItemData.material) {
			showAlert("Please select a material first");
			return;
		}

		if (!newItemData.quantity || newItemData.quantity === "0") {
			showAlert("Please enter a valid quantity");
			return;
		}

		const itemToAdd = {
			...newItemData,
			id: newItemData.id || Date.now(),
		};

		setItems((prev) => {
			const updatedItems = [...prev, itemToAdd];
			setSelectedItem(updatedItems.length - 1);
			return updatedItems;
		});
		setNewItemData(null);
	};

	const handleMaterialSelect = (material) => {
		if (newItemData) {
			// When adding new item
			setNewItemData({
				...newItemData,
				material: material.material,
				displayMaterial: material.displayMaterial,
				materialDescription: material.description,
				uom: material.uom,
				batch: material.batch,
				displayBatch: material.displayBatch,
				quantity: "",
			});
		} else {
			// When editing existing item
			setItems((prevItems) =>
				prevItems.map((item, index) =>
					index === selectedItem
						? {
								...item,
								material: material.material,
								displayMaterial: material.displayMaterial,
								materialDescription: material.description,
								uom: material.uom,
								batch: material.batch,
								displayBatch: material.displayBatch,
								quantity: "",
						  }
						: item
				)
			);
		}
		setIsMaterialSearchOpen(false);
	};

	const handleOrderSelect = (order) => {
		if (newItemData) {
			// Handling order selection for new item
			setNewItemData({
				...newItemData,
				order: order.aufnr,
			});
		} else {
			// Handling order selection for existing item
			setItems((prevItems) =>
				prevItems.map((item, index) =>
					index === selectedItem ? { ...item, order: order.aufnr } : item
				)
			);
		}
		setIsOrderSearchOpen(false);
	};

	const handleBack = () => {
		if (isSaving) return;

		if (
			items.length > 0 ||
			Object.values(mainData).some((value) => value !== ""
		)) {
			setShowConfirmDialog(true);
		} else {
			navigate(-1);
		}
	};

	const handleConfirmBack = () => {
		setShowConfirmDialog(false);
		navigate(-1);
	};

	const handleSubmit = async () => {
		if (isSaving) return;

		if (!mainData.plant || !mainData.storageLocation || items.length === 0) {
			showAlert("Please fill in all required fields and add at least one item");
			return;
		}

		if (category === "IO") {
			const missingOrders = items.some((item) => !item.order);
			if (missingOrders) {
				showAlert("Order field is required for all items when category is IO");
				return;
			}
		}

		try {
			setIsSaving(true);

			const payload = {
				header: {
					plant: mainData.plant,
					storage_location: mainData.storageLocation,
					material_slip: mainData.materialSlip || "",
					bktxt: mainData.docHeaderText || "",
					document_date: mainData.documentDate,
				},
				details: items.map((item, index) => ({
					item: (index + 1).toString(),
					material: item.material,
					material_description: item.materialDescription || "",
					quantity: Number(item.quantity),
					uom: item.uom?.toUpperCase() === "PCS" ? "ST" : item.uom || "",
					wempf: item.recipient || "",
					order: item.order || "",
					text: item.text || "",
					charg: item.batch || "",
					status: item.status || "",
				})),
			};

			// console.log(payload);

			const response = await axios.post(
				getApiUrl(`/sparepart/${category}`),
				payload,
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				}
			);

			// Show success message from response
			showAlert(response.data.message || "Sparepart document created successfully!", "success", () => {
				navigate("/");
			});

		} catch (error) {
			// console.error("Error submitting sparepart:", error);

			if (error.response?.data?.message && Array.isArray(error.response.data.message)) {
				const errorMessages = error.response.data.message;
				if (errorMessages.length > 0) {
					showAlert(
						<ul className="list-disc pl-5 space-y-1">
							{errorMessages.map((msg, index) => (
								<li key={index}>{msg}</li>
							))}
						</ul>
					);
				} else {
					showAlert("Failed to create sparepart document. Please try again.");
				}
			} else {
				showAlert("Failed to create sparepart document. Please try again.");
			}

			if (error.response?.status === 401) {
				localStorage.removeItem("token");
				navigate("/login");
			}
		} finally {
			setIsSaving(false);
		}
	};

	const handleStorageLocationSelect = (value) => {
		handleMainDataChange("storageLocation", value);
		handleMainDataChange("plant", "1101"); // Set default plant
		setIsStorageLocationOpen(false);
	};

	useEffect(() => {
		// If materials were passed, add them to the sparepart list
		if (materials && materials.length > 0) {
			const newMaterials = materials.map((material, index) => ({
				id: Date.now() + Math.random(),
				item: (index + 1).toString().padStart(2, "0"),
				material: material.matnr,
				displayMaterial: stripLeadingZeros(material.matnr),
				materialDescription: material.maktx,
				quantity: material.menge,
				uom: material.uom,
				recipient: "",
				order: "",
				text: "",
				batch: material.charg || "",
				displayBatch: material.charg ? stripLeadingZeros(material.charg) : "",
			}));

			// Add these materials to your form state
			setItems(newMaterials);
			setSelectedItem(0);

			// Set plant and storage location from the first material
			if (materials[0] && !mainData.plant && !mainData.storageLocation) {
				setMainData((prev) => ({
					...prev,
					plant: materials[0].werks,
					storageLocation: materials[0].lgort,
				}));
			}

			// Automatically set category to CGI for NV type materials
			if (type === "NV" && (category === "" || category == null)) {
				location.state.category = "CGI";
			}
		}
	}, [materials, type, category, mainData.plant, mainData.storageLocation, location.state]);
	// Move the token validation useEffect to the top
	useEffect(() => {
		const validateToken = async () => {
			setIsValidatingToken(true);
			const isValid = await checkTokenValidity();
			if (!isValid) {
				navigate("/login");
				return;
			}
			setIsValidatingToken(false);
		};

		validateToken();
	}, [navigate]);

	if (isValidatingToken) {
		return <LoadingScreen />;
	}

	return (
		<div className="min-h-screen bg-gray-100">
			{isSaving && <LoadingOverlay />}
			<nav className="bg-white shadow-lg">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex justify-between h-16">
						<div className="flex items-center overflow-hidden">
							<h1 className="text-base sm:text-xl font-bold text-gray-900 truncate pr-4">
								Add New Sparepart ({type}) {type === "V" ? "-" : ""} {category}
							</h1>
						</div>
						<div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
							<button
								onClick={handleSubmit}
								disabled={isSaving}
								className={`px-3 sm:px-4 py-2 text-sm text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 
									${
										isSaving
											? "bg-gray-400 cursor-not-allowed"
											: "bg-green-600 hover:bg-green-700 focus:ring-green-500"
									}`}
							>
								{isSaving ? "Saving..." : "Save"}
							</button>
							<button
								onClick={handleBack}
								disabled={isSaving}
								className={`px-3 sm:px-4 py-2 text-sm text-gray-600 hover:text-gray-900 ${
									isSaving ? "cursor-not-allowed opacity-50" : ""
								}`}
							>
								Back
							</button>
						</div>
					</div>
				</div>
			</nav>

			<main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
				<div className="px-4 py-6 sm:px-0">
					<div className="bg-white shadow rounded-lg">
						{/* Header Information section */}
						<div className="px-4 py-5 sm:p-6 border-b border-gray-200">
							<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
								<TextField
									id="itemTotal"
									label="Item Total"
									value={items.length}
									onChange={() => {}}
									disabled={true}
									width="16"
									className="opacity-75"
								/>
								<DatePickerField
									label="Document Date"
									value={mainData.documentDate}
									onChange={handleDateChange}
									className="opacity-75"
								/>
								<TextField
									id="plant"
									label="Plant"
									value={mainData.plant}
									onChange={(value) => handleMainDataChange("plant", value)}
									disabled={items.length > 0}
								/>
								<TextField
									id="storageLocation"
									label="Storage Location"
									value={mainData.storageLocation}
									onChange={(value) =>
										handleMainDataChange("storageLocation", value)
									}
									disabled={true}
									rightIcon={
										!items.length ? (
											<MaterialSearchIcon
												onClick={() => setIsStorageLocationOpen(true)}
											/>
										) : null
									}
									forceWhite={!items.length}
								/>
								<TextField
									id="materialSlip"
									label="Material Slip"
									value={mainData.materialSlip}
									onChange={(value) =>
										handleMainDataChange("materialSlip", value)
									}
									maxLength={16}
								/>
								<TextField
									id="docHeaderText"
									label="Doc Header Text"
									value={mainData.docHeaderText}
									onChange={(value) =>
										handleMainDataChange("docHeaderText", value)
									}
									maxLength={25}
								/>
							</div>
						</div>

						{/* Item Details */}
						{!newItemData && items.length > 0 && (
							<div className="px-4 py-5 sm:p-6 border-b border-gray-200">
								<h3 className="text-lg font-medium text-gray-900 mb-4">
									Item Details
								</h3>
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
									<TextField
										label="Item"
										value={(selectedItem + 1).toString().padStart(2, "0")}
										disabled={true}
										width="16"
										className="opacity-75"
									/>
									<div className="sm:col-span-3">
										<TextField
											label="Material"
											value={items[selectedItem]?.displayMaterial || ""}
											readOnly
											onClick={() => setIsMaterialSearchOpen(true)}
											rightIcon={<MaterialSearchIcon onClick={() => setIsMaterialSearchOpen(true)} />}
										/>
										{items[selectedItem]?.materialDescription && (
											<div className="mt-1">
												<span className="text-xs text-gray-500">
													{items[selectedItem]?.materialDescription}
												</span>
											</div>
										)}
									</div>
									{type === "V" && (
										<TextField
											label="Batch"
											value={items[selectedItem]?.displayBatch || "-"}
											disabled={true}
										/>
									)}
									<div className="flex gap-2 sm:col-span-2">
										<div className="w-2/3">
											<TextField
												label="Quantity"
												value={items[selectedItem]?.quantity || ""}
												onChange={(value) => handleItemChange("quantity", value, selectedItem)}
												maxLength={13}
											/>
										</div>
										<div className="w-1/3">
											<TextField
												label="UoM"
												value={items[selectedItem]?.uom || ""}
												disabled={true}
											/>
										</div>
									</div>
									<div className="sm:col-span-2">
										<TextField
											label="Recipient"
											value={items[selectedItem]?.recipient || ""}
											onChange={(value) => handleItemChange("recipient", value, selectedItem)}
											maxLength={12}
										/>
									</div>
									{(category === "IO" || category === "CGI") && (
										<div className="sm:col-span-4">
											<TextField
												label={`Order${category === "CGI" ? " (Optional)" : ""}`}
												value={items[selectedItem]?.order || ""}
												readOnly
												onClick={() => setIsOrderSearchOpen(true)}
												rightIcon={
													<div className="flex items-center space-x-1">
														{items[selectedItem]?.order && (
															<button
																onClick={(e) => {
																	e.stopPropagation();
																	handleItemChange("order", "", selectedItem);
																}}
																className="w-4 h-4 text-gray-500 hover:text-gray-700"
															>
																<XMarkIcon className="w-4 h-4" />
															</button>
														)}
														<MaterialSearchIcon onClick={() => setIsOrderSearchOpen(true)} />
													</div>
												}
											/>
										</div>
									)}
									<div className="sm:col-span-4">
										<TextField
											label="Text"
											value={items[selectedItem]?.text || ""}
											onChange={(value) => handleItemChange("text", value, selectedItem)}
											maxLength={50}
										/>
									</div>
								</div>
							</div>
						)}

						{/* New Item Form - Show when adding new item */}
						{newItemData && (
							<div className="px-4 py-5 sm:p-6 border-b border-gray-200">
								<div className="flex justify-between items-center mb-4">
									<h3 className="text-lg font-medium text-gray-900">
										New Item
									</h3>
									<div className="space-x-2">
										<button
											onClick={handleAddItemToList}
											className="px-3 py-1 text-sm text-white bg-green-600 hover:bg-green-700 rounded-md"
										>
											Add to List
										</button>
										<button
											onClick={() => setNewItemData(null)}
											className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
										>
											Cancel
										</button>
									</div>
								</div>
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
									<div className="sm:col-span-2">
										<TextField
											label="Material"
											value={newItemData.material}
											onChange={() => {}}
											disabled={true}
											forceWhite={true}
											rightIcon={
												<MaterialSearchIcon
													onClick={handleMaterialSearchClick}
												/>
											}
										/>
										{newItemData.material && (
											<div className="mt-1">
												<span className="text-xs text-gray-500">
													{newItemData.materialDescription}
												</span>
											</div>
										)}
									</div>
									{type === "V" && (
										<div className="sm:col-span-2">
											<TextField
												label="Batch"
												value={newItemData.batch}
												disabled={true}
											/>
										</div>
									)}
									<div className="flex gap-2 sm:col-span-2">
										<div className="w-2/3">
											<TextField
												label="Quantity"
												value={newItemData.quantity}
												onChange={(value) =>
													setNewItemData({
														...newItemData,
														quantity: value,
													})
												}
												width="full"
											/>
										</div>
										<div className="w-1/3">
											<TextField
												label="UoM"
												value={newItemData.uom}
												disabled={true}
												width="full"
											/>
										</div>
									</div>
									<div className="sm:col-span-2">
										<TextField
											label="Recipient"
											value={newItemData.recipient}
											onChange={(value) =>
												setNewItemData({
													...newItemData,
													recipient: value,
												})
											}
											maxLength={12}
										/>
									</div>
									{(category === "IO" || type === "NV") && (
										<div className="sm:col-span-4">
											<TextField
												label={`Order${category === "IO" ? "" : " (Optional)"}`}
												value={newItemData.order}
												onChange={(value) =>
													setNewItemData({
														...newItemData,
														order: value,
													})
												}
												disabled={true}
												forceWhite={true}
												rightIcon={
													<div className="flex items-center">
														<MaterialSearchIcon
															onClick={() => setIsOrderSearchOpen(true)}
														/>
														{category === "CGI" && newItemData.order && (
															<ClearIcon
																onClick={(e) => {
																	e.stopPropagation();
																	setNewItemData({
																		...newItemData,
																		order: "",
																	});
																}}
															/>
														)}
													</div>
												}
											/>
										</div>
									)}
									<div className="sm:col-span-4">
										<TextField
											label="Text"
											value={newItemData.text}
											onChange={(value) =>
												setNewItemData({
													...newItemData,
													text: value,
												})
											}
											maxLength={50}
										/>
									</div>
								</div>
							</div>
						)}

						{/* Items List */}
						<div className="px-4 py-5 sm:p-6">
							<div className="flex justify-between items-center mb-4">
								<h3 className="text-lg font-medium text-gray-900">
									Items List
								</h3>
								{!newItemData && !isSaving && (
									<button
										onClick={handleAddNewItem}
										className="px-3 py-1 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md"
									>
										Add New Item
									</button>
								)}
							</div>

							{/* Desktop View */}
							<div className="hidden md:block overflow-x-auto">
								<table className="min-w-full divide-y divide-gray-200">
									<thead className="bg-gray-50">
										<tr>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
												Item
											</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Material
											</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Quantity
											</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
												UoM
											</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Plant
											</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												SLoc
											</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Recipient
											</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Order
											</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Text
											</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Actions
											</th>
										</tr>
									</thead>
									<tbody className="bg-white divide-y divide-gray-200">
										{items.map((item, index) => (
											<tr
												key={item.id}
												className={`hover:bg-gray-50 cursor-pointer ${
													selectedItem === index ? "bg-blue-50" : ""
												}`}
												onClick={() => setSelectedItem(index)}
											>
												<td className="px-3 py-2 whitespace-nowrap text-sm">
													{item.item}
												</td>
												<td className="px-3 py-2 text-sm">
													<div>
														<div>{item.displayMaterial}</div>
														<div className="text-xs text-gray-500">
															{item.materialDescription}
														</div>
													</div>
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm">
													{item.quantity}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm">
													{item.uom}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm">
													{mainData.plant}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm">
													{mainData.storageLocation}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm">
													{item.recipient || "-"}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm">
													{item.order || "-"}
												</td>
												<td className="px-3 py-2 text-sm">
													{item.text || "-"}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm">
													<button
														onClick={(e) => {
															e.stopPropagation();
															handleDeleteItem(index);
														}}
														disabled={isSaving}
														className={`inline-flex items-center px-2 py-1 rounded-md text-red-600 hover:text-red-900 ${
															isSaving ? "opacity-50 cursor-not-allowed" : ""
														}`}
													>
														<XMarkIcon className="h-5 w-5" />
														<span className="ml-1">Remove</span>
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{/* Mobile view */}
							<div className="md:hidden">
								<div className="space-y-2">
									{items.map((item, index) => (
										<div
											key={item.id}
											className={`border rounded-lg p-4 ${
												selectedItem === index
													? "bg-blue-50 border-blue-200"
													: "bg-white hover:bg-gray-50"
											}`}
											onClick={() => setSelectedItem(index)}
										>
											{/* Header with Item number and Quantity/UoM/Delete */}
											<div className="flex justify-between items-center mb-2">
												<span className="text-sm text-blue-600">
													Item {item.item}
												</span>
												<div className="flex items-center space-x-2">
													<span className="text-sm">
														{item.quantity} {item.uom}
													</span>
													<button
														onClick={(e) => {
															e.stopPropagation();
															handleDeleteItem(index);
														}}
														className="inline-flex items-center p-1 rounded-md text-red-600"
													>
														<XMarkIcon className="h-4 w-4" />
													</button>
												</div>
											</div>

											{/* Material and Description */}
											<div className="mb-2">
												<div className="flex justify-between items-center">
													<span className="text-sm text-gray-500">
														Material
													</span>
													<span className="text-sm">
														{item.displayMaterial}
													</span>
												</div>
												<div className="text-sm text-gray-600 mt-1">
													{item.materialDescription}
												</div>
											</div>

											{/* Batch - only for V type */}
											{type === "V" && (
												<div className="flex justify-between items-center mb-2">
													<span className="text-sm text-gray-500">Batch</span>
													<span className="text-sm">
														{item.displayBatch || "-"}
													</span>
												</div>
											)}

											{/* Plant/SLoc */}
											<div className="flex justify-between items-center mb-2">
												<span className="text-sm text-gray-500">
													Plant/SLoc
												</span>
												<span className="text-sm">
													{mainData.plant}/{mainData.storageLocation}
												</span>
											</div>

											{/* Recipient */}
											<div className="flex justify-between items-center mb-2">
												<span className="text-sm text-gray-500">Recipient</span>
												<span className="text-sm">{item.recipient || "-"}</span>
											</div>

											{/* Order - only for IO or NV type */}
											{(category === "IO" || type === "NV") && (
												<div className="flex justify-between items-center mb-2">
													<span className="text-sm text-gray-500">Order</span>
													<span className="text-sm">{item.order || "-"}</span>
												</div>
											)}

											{/* Text */}
											<div className="flex justify-between items-center">
												<span className="text-sm text-gray-500">Text</span>
												<span className="text-sm">{item.text || "-"}</span>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			</main>

			{/* Confirmation Dialog */}
			<div
				className={`fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity ${
					showConfirmDialog ? "block" : "hidden"
				}`}
			>
				<div className="fixed inset-0 z-10 overflow-y-auto">
					<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
						<div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transform transition-all sm:my-8 sm:w-full sm:max-w-lg">
							<div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									<div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
										<h3 className="text-base font-semibold leading-6 text-gray-900">
											Unsaved Changes
										</h3>
										<div className="mt-2">
											<p className="text-sm text-gray-500">
												Are you sure you want to leave? Your unsaved changes
												will be lost.
											</p>
										</div>
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
								<button
									type="button"
									onClick={handleConfirmBack}
									className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
								>
									Leave
								</button>
								<button
									type="button"
									onClick={() => setShowConfirmDialog(false)}
									className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
								>
									Cancel
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<MaterialSearchModal
				isOpen={isMaterialSearchOpen}
				onClose={() => setIsMaterialSearchOpen(false)}
				onSelect={handleMaterialSelect}
				plant={mainData.plant}
				storageLocation={mainData.storageLocation}
				type={type}
				existingItems={items}
				category={category}
				navigate={memoizedNavigate}
			/>

			<OrderSearchModal
				isOpen={isOrderSearchOpen}
				onClose={() => setIsOrderSearchOpen(false)}
				onSelect={handleOrderSelect}
				navigate={memoizedNavigate}
			/>

			<StorageLocationModal
				isOpen={isStorageLocationOpen}
				onClose={() => setIsStorageLocationOpen(false)}
				onSelect={handleStorageLocationSelect}
			/>

			{/* Alert Dialog */}
			{alertDialog.isOpen && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<div 
							className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
							onClick={() => {
								setAlertDialog((prev) => ({ ...prev, isOpen: false }));
								if (alertDialog.onClose) {
									alertDialog.onClose();
								}
							}}
						/>

						<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
							<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									<div
										className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
											alertDialog.type === "success"
												? "bg-green-100"
												: "bg-red-100"
										}`}
									>
										{alertDialog.type === "success" ? (
											<svg
												className="h-6 w-6 text-green-600"
												xmlns="http://www.w3.org/2000/svg"
												fill="none"
												viewBox="0 0 24 24"
												strokeWidth="1.5"
												stroke="currentColor"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M4.5 12.75l6 6 9-13.5"
												/>
											</svg>
										) : (
											<svg
												className="h-6 w-6 text-red-600"
												xmlns="http://www.w3.org/2000/svg"
												fill="none"
												viewBox="0 0 24 24"
												strokeWidth="1.5"
												stroke="currentColor"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
												/>
											</svg>
										)}
									</div>
									<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
										<h3 className="text-lg leading-6 font-medium text-gray-900">
											{alertDialog.type === "success" ? "Success" : "Alert"}
										</h3>
										<div className="mt-2">
											{typeof alertDialog.message === "string" ? (
												<p className="text-sm text-gray-500">
													{alertDialog.message}
												</p>
											) : (
												<div className="text-sm text-gray-500">
													{alertDialog.message}
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
								<button
									type="button"
									className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${
										alertDialog.type === "success"
											? "bg-green-600 hover:bg-green-700 focus:ring-green-500"
											: "bg-red-600 hover:bg-red-700 focus:ring-red-500"
									}`}
									onClick={() => {
										setAlertDialog((prev) => ({ ...prev, isOpen: false }));
										if (alertDialog.onClose) {
											alertDialog.onClose();
										}
									}}
								>
									OK
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default AddSparepart;

